"use client";
import {
	RiEyeFill,
	RiEyeLine,
	RiFlagFill,
	RiFlagLine,
	RiHeartFill,
	RiHeartLine,
	RiLiveFill,
	RiLiveLine,
	RiMicFill,
	RiMicLine,
} from "@remixicon/react";
import { cn } from "@workspace/ui/lib/utils";

interface ReviewStatusButtonProps {
	label: string;
	icon: "mic" | "live" | "flag" | "eye" | "heart";
	isActive: boolean;
	activeClassName: string;
	inactiveClassName: string;
	onClick: () => void;
}

export function ReviewStatusButton({
	label,
	icon,
	isActive,
	activeClassName,
	inactiveClassName,
	onClick,
}: ReviewStatusButtonProps) {
	// Get the appropriate icon component based on icon type and active state
	const getIconComponent = () => {
		switch (icon) {
			case "mic":
				return isActive ? RiMicFill : RiMicLine;
			case "live":
				return isActive ? RiLiveFill : RiLiveLine;
			case "flag":
				return isActive ? RiFlagFill : RiFlagLine;
			case "eye":
				return isActive ? RiEyeFill : RiEyeLine;
			case "heart":
				return isActive ? RiHeartFill : RiHeartLine;
			default:
				return null;
		}
	};

	const IconComponent = getIconComponent();

	return (
		<button
			type="button"
			onClick={onClick}
			className={cn(
				// Base styles with enhanced border radius and pressed state
				"flex flex-col items-center gap-0.5 rounded-lg p-1 md:gap-1 md:p-2",
				// Smooth transitions like festival cards
				"transition-all duration-300 ease-out",
				// Enhanced pressed state animation
				"active:scale-[0.99] active:transition-transform active:duration-150",
				// Conditional styles
				isActive ? activeClassName : inactiveClassName,
			)}
			aria-pressed={isActive}
			title={label} // Use label for tooltip
		>
			{IconComponent && (
				<IconComponent
					className={cn(
						// Base icon styles with enhanced transitions
						"text-lg md:text-xl",
						// Smooth color and scale transitions like festival cards
						"transition-all duration-300 ease-out",
						// Enhanced active state animation
						isActive && "scale-110",
						// Color with smooth transition
						isActive ? "" : "text-muted-foreground",
					)}
					size={24}
					aria-hidden="true"
				/>
			)}
			<span
				className={cn(
					"text-xs transition-colors duration-300 ease-out",
					isActive ? "" : "text-muted-foreground",
				)}
			>
				{label}
			</span>
		</button>
	);
}
